import { z } from 'zod'
import { amountMinor, currencyCode, isoDateTime, uuid } from './common.js'

/**
 * "What needs you, by when" — deadlines — and the cost lines they may point at.
 *
 * The rule that matters everywhere in this file: money is an integer minor-unit
 * amount plus an ISO-4217 code, never a float, and never divided before it is
 * displayed. Totals are computed PER CURRENCY in the service layer
 * (`money.service.ts`) and there is no conversion anywhere in this system — a
 * trip billed in both BRL and USD produces two totals, not a blended one.
 */

export const deadlineOwner = z.enum(['advisor', 'traveller'])
export type DeadlineOwner = z.infer<typeof deadlineOwner>

export const deadlineStatus = z.enum(['open', 'done', 'missed'])
export type DeadlineStatus = z.infer<typeof deadlineStatus>

export const costStatus = z.enum(['quoted', 'committed', 'paid'])
export type CostStatus = z.infer<typeof costStatus>

// ---------------------------------------------------------------------------
// Deadlines
// ---------------------------------------------------------------------------

const deadlineFields = {
  label: z.string().trim().min(1).max(200),
  dueAt: isoDateTime,
  owner: deadlineOwner.default('traveller'),
  /** Optional link to the payment this deadline is about. Must be on the same trip. */
  costLineId: uuid.nullish(),
}

export const createDeadlineRequest = z.object(deadlineFields)
export type CreateDeadlineRequest = z.infer<typeof createDeadlineRequest>

export const updateDeadlineRequest = z
  .object({ ...deadlineFields, status: deadlineStatus })
  .partial()
export type UpdateDeadlineRequest = z.infer<typeof updateDeadlineRequest>

export const deadline = z.object({
  id: uuid,
  tripId: uuid,
  label: z.string(),
  dueAt: isoDateTime,
  owner: deadlineOwner,
  costLineId: uuid.nullable(),
  status: deadlineStatus,
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
})
export type Deadline = z.infer<typeof deadline>

// ---------------------------------------------------------------------------
// Cost lines
// ---------------------------------------------------------------------------

const costLineFields = {
  label: z.string().trim().min(1).max(200),
  /** Integer minor units — centavos, cents. Never a float. */
  amountMinor,
  /** ISO-4217, e.g. BRL, USD, EUR. */
  currency: currencyCode,
  status: costStatus.default('quoted'),
  dueAt: isoDateTime.nullish(),
  /** Loose anchor to a reservation or place, kept optional on purpose. */
  entityType: z.string().trim().min(1).max(60).nullish(),
  entityId: uuid.nullish(),
}

export const createCostLineRequest = z.object(costLineFields)
export type CreateCostLineRequest = z.infer<typeof createCostLineRequest>

export const updateCostLineRequest = z.object(costLineFields).partial()
export type UpdateCostLineRequest = z.infer<typeof updateCostLineRequest>

export const costLine = z.object({
  id: uuid,
  tripId: uuid,
  label: z.string(),
  amountMinor,
  currency: currencyCode,
  status: costStatus,
  dueAt: isoDateTime.nullable(),
  entityType: z.string().nullable(),
  entityId: uuid.nullable(),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
})
export type CostLine = z.infer<typeof costLine>

// ---------------------------------------------------------------------------
// Totals
// ---------------------------------------------------------------------------

/**
 * A running total for one currency. There is deliberately no field anywhere in
 * this contract that lets two of these merge into one number — a second
 * currency on a trip means a second entry in this array, not a converted sum.
 */
export const currencyTotal = z.object({
  currency: currencyCode,
  totalMinor: amountMinor,
})
export type CurrencyTotal = z.infer<typeof currencyTotal>

/** Everything the trip page's money card needs, in one read. */
export const moneySummary = z.object({
  deadlines: z.array(deadline),
  costLines: z.array(costLine),
  totals: z.array(currencyTotal),
})
export type MoneySummary = z.infer<typeof moneySummary>
